import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для получения уведомлений
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      // Вызываем API с параметрами через query string
      const response = await socialAPI.getNotifications(limit, offset);
      
      // Проверяем, что response и response.data существуют
      if (!response) {
        console.warn('fetchNotifications: no response');
        return { notifications: [], hasMore: false };
      }
      
      // Проверяем различные форматы ответа
      let data = response.data;
      
      // Если data null или undefined, возвращаем пустой массив
      if (data === null || data === undefined) {
        console.warn('fetchNotifications: response.data is null or undefined');
        return { notifications: [], hasMore: false };
      }
      
      // Если data - это массив напрямую
      if (Array.isArray(data)) {
        return {
          notifications: data,
          hasMore: data.length >= limit
        };
      }
      
      // Если data - это объект с notifications
      if (typeof data === 'object' && data.notifications) {
        return {
          notifications: Array.isArray(data.notifications) ? data.notifications : [],
          hasMore: typeof data.hasMore === 'boolean' ? data.hasMore : (Array.isArray(data.notifications) && data.notifications.length >= limit)
        };
      }
      
      // Fallback: возвращаем пустой массив
      console.warn('fetchNotifications: unexpected data format', data);
      return { notifications: [], hasMore: false };
    } catch (error) {
      console.error('fetchNotifications error:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch notifications');
    }
  }
);

// Async thunk для получения количества непрочитанных уведомлений
export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getUnreadCount();
      return response.data.unread_count;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch unread count');
    }
  }
);

// Async thunk для отметки уведомления как прочитанного
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      await socialAPI.markNotificationAsRead(notificationId);
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark notification as read');
    }
  }
);

// Async thunk для отметки всех уведомлений как прочитанных
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await socialAPI.markAllNotificationsAsRead();
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark all notifications as read');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    hasMore: true,
    lastFetchTime: null,
  },
  reducers: {
    // Добавление нового уведомления (приходит через WebSocket)
    addNotification: (state, action) => {
      const notification = action.payload;
      
      // Проверяем, что payload существует и является объектом
      if (!notification || typeof notification !== 'object') {
        console.warn('addNotification: invalid payload', notification);
        return;
      }
      
      // Если нет id, создаем временный (для уведомлений из WebSocket, которые еще не сохранены в БД)
      if (!notification.id) {
        notification.id = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      
      // Проверяем, нет ли уже такого уведомления
      const exists = state.notifications.find(n => n.id === notification.id);
      if (!exists) {
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        // Ограничиваем количество уведомлений в памяти
        if (state.notifications.length > 100) {
          state.notifications = state.notifications.slice(0, 100);
        }
      }
    },

    // Обновление уведомления
    updateNotification: (state, action) => {
      const payload = action.payload;
      
      // Проверяем, что payload существует и содержит id и updates
      if (!payload || typeof payload !== 'object' || !payload.id || !payload.updates) {
        console.warn('updateNotification: invalid payload', payload);
        return;
      }
      
      const { id, updates } = payload;
      const index = state.notifications.findIndex(n => n.id === id);
      if (index !== -1) {
        state.notifications[index] = { ...state.notifications[index], ...updates };
      }
    },

    // Удаление уведомления
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      
      if (notification && !notification.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      
      state.notifications = state.notifications.filter(n => n.id !== notificationId);
    },

    // Локальная отметка как прочитанного (оптимистичное обновление)
    markAsReadOptimistic: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      
      if (notification && !notification.isRead) {
        notification.isRead = true;
        notification.readAt = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // Локальная отметка всех как прочитанных
    markAllAsReadOptimistic: (state) => {
      state.notifications.forEach(notification => {
        if (!notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
        }
      });
      state.unreadCount = 0;
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.isLoading = false;
      state.error = null;
      state.hasMore = true;
      state.lastFetchTime = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Получение уведомлений
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Проверяем, что payload существует и является объектом
        if (!action.payload || typeof action.payload !== 'object') {
          console.warn('fetchNotifications.fulfilled: invalid payload', action.payload);
          return;
        }
        
        const { notifications = [], hasMore = false } = action.payload;
        
        // Убеждаемся, что notifications - это массив
        if (!Array.isArray(notifications)) {
          console.warn('fetchNotifications.fulfilled: notifications is not an array', notifications);
          return;
        }
        
        // Если это первая загрузка, заменяем список
        if (state.notifications.length === 0) {
          state.notifications = notifications;
        } else {
          // Иначе добавляем новые уведомления
          const existingIds = new Set(state.notifications.map(n => n.id));
          const newNotifications = notifications.filter(n => !existingIds.has(n.id));
          state.notifications = [...state.notifications, ...newNotifications];
        }
        
        state.hasMore = hasMore;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch notifications';
      })
      
      // Получение количества непрочитанных
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        // Проверяем, что payload существует и является числом
        if (typeof action.payload === 'number') {
          state.unreadCount = Math.max(0, action.payload);
        } else {
          console.warn('fetchUnreadCount.fulfilled: invalid payload', action.payload);
        }
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        // Не логируем ошибку, если это просто сетевая ошибка или 404
        // Это не критично для работы приложения
        const errorMessage = action.payload || action.error?.message || '';
        // Подавляем логирование ошибок уведомлений, так как это не критично
        // if (errorMessage && !errorMessage.includes('404') && !errorMessage.includes('Network')) {
        //   console.warn('Failed to fetch unread count:', errorMessage);
        // }
        // Не изменяем состояние при ошибке - оставляем текущее значение unreadCount
      })
      
      // Отметка как прочитанного
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        console.error('Failed to mark notification as read:', action.payload);
      })
      
      // Отметка всех как прочитанных
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach(notification => {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
        });
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        console.error('Failed to mark all notifications as read:', action.payload);
      });
  },
});

// Экспортируем actions
export const {
  addNotification,
  updateNotification,
  removeNotification,
  markAsReadOptimistic,
  markAllAsReadOptimistic,
  clearError,
  resetNotifications,
} = notificationSlice.actions;

// Селекторы
export const selectNotifications = (state) => state.notifications.notifications;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state) => state.notifications.isLoading;
export const selectNotificationsError = (state) => state.notifications.error;
export const selectHasMoreNotifications = (state) => state.notifications.hasMore;
export const selectUnreadNotifications = (state) => 
  state.notifications.notifications.filter(n => !n.isRead);

export default notificationSlice.reducer;
